import { useCallback } from "react";
import { IUser } from "../../application/models";
import { useAxiosInstances } from "../axios-instance";
import { IDownloadLog } from "../../application/models/IDownloadLog";

export const useUserApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();

  const findById = useCallback(
    (data: { userId: string }): Promise<IUser> => {
      return axiosAuthInstance
        .get("/routes/user/" + data.userId)
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const changeUserEmailApi = useCallback(
    (data: { userId: string; email: string }): Promise<IUser> => {
      return axiosAuthInstance
        .put("/routes/user/email", {
          id: data.userId,
          email: data.email,
        })
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const changeUserNameApi = useCallback(
    (data: { userId: string; name: string }): Promise<IUser> => {
      return axiosAuthInstance
        .put("/routes/user/name", {
          id: data.userId,
          name: data.name,
        })
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const changeUserPasswordApi = useCallback(
    (data: { userId: string; password: string }): Promise<IUser> => {
      return axiosAuthInstance
        .put("/routes/user/password", {
          id: data.userId,
          password: data.password,
        })
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const updateUserApi = useCallback(
    (data: {
      userId: string;
      name?: string;
      email?: string;
      userType?: string;
      status?: boolean;
      image?: any;
      allowedMasterCompanies?: string[];
    }): Promise<IUser> => {
      return axiosAuthInstance
        .put("/routes/user", {
          id: data.userId,
          name: data.name,
          email: data.email,
          userType: data.userType,
          status: data.status,
          img: data.image,
          allowedMasterCompanies: data.allowedMasterCompanies,
        })
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const deleteUserApi = useCallback(
    (data: { userId: string }): Promise<IUser> => {
      return axiosAuthInstance
        .delete("/routes/user/" + data.userId)
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const findUserByCompanyIdApi = useCallback(
    (data: { companyId: string; userId?: string }): Promise<IUser[]> => {
      return axiosAuthInstance
        .get("/routes/user/list", {
          params: {
            companyId: data.companyId,
            userId: data.userId,
          },
        })
        .then((response) => {
          const users = response.data?.result?.userList;
          if (users) {
            return users;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const createUserApi = useCallback(
    (data: {
      userType: string;
      companyId: string;
      name: string;
      email: string;
      img?: string;
      allowedMasterCompanies?: string[];
    }): Promise<IUser> => {
      return axiosAuthInstance
        .post("/routes/user", {
          userType: data.userType,
          companyId: data.companyId,
          name: data.name,
          email: data.email,
          img: data.img,
          allowedMasterCompanies: data.allowedMasterCompanies,
        })
        .then((response) => {
          const user = response.data?.result?.user;
          if (user) {
            return user;
          } else {
            throw {
              type: "NotFoundError",
              message: "Usuário não encontrado",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.error) {
            throw {
              message: error.response.data.error.message,
              type: error.response.data.error.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const getLastDownloadLog = useCallback(
    (data: { userId: string }): Promise<IDownloadLog | undefined> => {
      return axiosAuthInstance
        .get("/routes/user/last-download", {
          params: {
            userId: data.userId,
          },
        })
        .then((response) => {
          const downloadLog = response.data?.result?.downloadLog;
          if (downloadLog) {
            return downloadLog;
          }
        });
    },
    [axiosAuthInstance]
  );

  const findUsersBySearchTermApi = useCallback(
    (data: {
      userId: string;
      searchTerm?: string;
      page?: number;
    }): Promise<{
      usersData: {
        userId: string;
        email: string;
        name: string;
      }[];
    }> => {
      return axiosAuthInstance
        .get("/routes/user/users", {
          params: {
            ...data,
          },
        })
        .then((res) => {
          const result = res.data.result;

          if (result) {
            return result;
          } else {
            throw new Error("Erro ao buscar usuários");
          }
        });
    },
    [axiosAuthInstance]
  );

  const resetPasswordApi = useCallback(
    (data: { userId: string }): Promise<string> => {
      return axiosAuthInstance
        .put("/routes/user/reset-password", {
          id: data.userId,
        })
        .then((response) => {
          const password = response.data?.result?.password;
          if (password) {
            return password; 
          } else {
            throw new Error("Senha temporária não foi gerada");
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result?.message) {
            throw new Error(error.response.data.result.message);
          } else if (error?.message) {
            throw error;
          } else {
            throw new Error("Erro desconhecido ao redefinir senha");
          }
        });
    },
    [axiosAuthInstance]
  );
  return {
    findById,
    changeUserEmailApi,
    changeUserNameApi,
    changeUserPasswordApi,
    updateUserApi,
    deleteUserApi,
    findUserByCompanyIdApi,
    createUserApi,
    getLastDownloadLog,
    findUsersBySearchTermApi,
    resetPasswordApi,
  };
};
